import { useState, useEffect, type ElementType } from "react";
import { BookOpen } from "lucide-react";
import { Header } from "@/components/Header";
import { useAuth } from "@/contexts/AuthContext";
import { SubjectAnalytics } from "@/components/SubjectAnalytics";
import {
  ChevronDown, ChevronUp, ChevronRight, Play, BookOpenCheck,
  BarChart3, ArrowLeft,
} from "lucide-react";
import { useSubjects } from "@/contexts/SubjectsContext";

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────
type Difficulty = "all" | "easy" | "medium" | "hard";

const DIFF_OPTIONS: { value: Difficulty; label: string; active: string; inactive: string }[] = [
  { value: "all",    label: "All",    active: "bg-[#1a3a6b] text-white",               inactive: "text-[#5a6a7e] hover:bg-[#1a3a6b]/10 hover:text-[#1a3a6b]" },
  { value: "easy",   label: "Easy",   active: "bg-green-100 text-green-700 ring-1 ring-green-300", inactive: "text-[#5a6a7e] hover:bg-green-50 hover:text-green-700" },
  { value: "medium", label: "Medium", active: "bg-yellow-100 text-yellow-700 ring-1 ring-yellow-300", inactive: "text-[#5a6a7e] hover:bg-yellow-50 hover:text-yellow-700" },
  { value: "hard",   label: "Hard",   active: "bg-red-100 text-red-700 ring-1 ring-red-300", inactive: "text-[#5a6a7e] hover:bg-red-50 hover:text-red-700" },
];

// ─────────────────────────────────────────────
// Difficulty picker
// ─────────────────────────────────────────────
function DifficultyPicker({ value, onChange }: { value: Difficulty; onChange: (d: Difficulty) => void }) {
  return (
    <div className="flex items-center gap-1">
      {DIFF_OPTIONS.map(opt => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`px-2 py-0.5 rounded-full text-xs font-medium transition-all ${
            value === opt.value ? opt.active : opt.inactive
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function topicColor(accuracy: number): string {
  if (accuracy >= 80) return "#22c55e";
  if (accuracy >= 60) return "#f59e0b";
  return "#ef4444";
}

function practiceUrl(subjectId: string, moduleId: string, topicId: string, difficulty: Difficulty, subtopicId?: string) {
  const p = new URLSearchParams({ subject: subjectId, module: moduleId, topic: topicId });
  if (subtopicId) p.set("subtopic", subtopicId);
  if (difficulty !== "all") p.set("difficulty", difficulty);
  return `/practice?${p}`;
}

function reviewUrl(subjectId: string, moduleId: string, topicId: string, subtopicId?: string) {
  const p = new URLSearchParams({ subject: subjectId, module: moduleId, topic: topicId });
  if (subtopicId) p.set("subtopic", subtopicId);
  return `/review?${p}`;
}

// ─────────────────────────────────────────────
// SubtopicRow
// ─────────────────────────────────────────────
function SubtopicRow({
  subjectId, moduleId, topicId, subtopicId, subtopicName,
}: {
  subjectId: string; moduleId: string; topicId: string;
  subtopicId: string; subtopicName: string;
}) {
  const [diff, setDiff] = useState<Difficulty>("all");
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 pl-10 pr-4 py-2.5 border-t border-[#eef1f6] hover:bg-[#f8f9fc] transition-colors">
      <span className="text-sm text-[#1a2435] flex-1">{subtopicName}</span>
      <DifficultyPicker value={diff} onChange={setDiff} />
      <div className="flex gap-1.5">
        <button
          onClick={() => window.location.href = reviewUrl(subjectId, moduleId, topicId, subtopicId)}
          className="flex items-center gap-1 px-3 h-7 text-xs font-semibold rounded-lg border border-[#dde3ed] text-[#5a6a7e] hover:border-[#1a3a6b] hover:text-[#1a3a6b] transition-colors"
        >
          <BookOpenCheck className="w-3 h-3" /> Review
        </button>
        <button
          onClick={() => window.location.href = practiceUrl(subjectId, moduleId, topicId, diff, subtopicId)}
          className="flex items-center gap-1 px-3 h-7 text-xs font-semibold rounded-lg bg-[#1a3a6b] text-white hover:bg-[#12265a] transition-colors"
        >
          <Play className="w-3 h-3" /> Practice
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// TopicRow
// ─────────────────────────────────────────────
function TopicRow({
  subjectId, moduleId, topicId, topicName, subtopics, topicStats,
}: {
  subjectId: string; moduleId: string; topicId: string; topicName: string;
  subtopics?: { id: string; name: string }[];
  topicStats?: { attempted: number; correct: number; accuracy: number } | null;
}) {
  const [open, setOpen] = useState(false);
  const [diff, setDiff] = useState<Difficulty>("all");
  const hasSubtopics = subtopics && subtopics.length > 0;
  const hasStats = topicStats && topicStats.attempted > 0;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-3 hover:bg-[#f8f9fc] transition-colors group">
        <button
          onClick={() => hasSubtopics && setOpen(v => !v)}
          className={`flex items-center gap-2 flex-1 text-left ${hasSubtopics ? "cursor-pointer" : "cursor-default"}`}
        >
          {hasSubtopics
            ? (open
                ? <ChevronDown className="w-4 h-4 text-[#5a6a7e] shrink-0" />
                : <ChevronRight className="w-4 h-4 text-[#5a6a7e] shrink-0" />)
            : <span className="w-4" />
          }
          <span className="font-semibold text-[#1a2435] group-hover:text-[#1a3a6b] transition-colors text-sm">
            {topicName}
          </span>
          {hasSubtopics && (
            <span className="text-xs text-[#5a6a7e]">({subtopics.length} subtopics)</span>
          )}
        </button>

        {/* Inline accuracy pill */}
        {hasStats && (
          <div className="flex items-center gap-2 pl-6 sm:pl-0">
            <span
              className="text-xs font-bold px-2 py-0.5 rounded-full"
              style={{
                color: topicColor(topicStats.accuracy),
                background: topicColor(topicStats.accuracy) + "18",
              }}
            >
              {topicStats.accuracy.toFixed(0)}% · {topicStats.attempted} Qs
            </span>
          </div>
        )}

        <div className="flex items-center gap-2 pl-6 sm:pl-0">
          <DifficultyPicker value={diff} onChange={setDiff} />
          <button
            onClick={() => window.location.href = reviewUrl(subjectId, moduleId, topicId)}
            className="flex items-center gap-1 px-3 h-7 text-xs font-semibold rounded-lg border border-[#dde3ed] text-[#5a6a7e] hover:border-[#1a3a6b] hover:text-[#1a3a6b] transition-colors"
          >
            <BookOpenCheck className="w-3 h-3" /> Review
          </button>
          <button
            onClick={() => window.location.href = practiceUrl(subjectId, moduleId, topicId, diff)}
            className="flex items-center gap-1 px-3 h-7 text-xs font-semibold rounded-lg bg-[#1a3a6b] text-white hover:bg-[#12265a] transition-colors"
          >
            <Play className="w-3 h-3" /> Practice
          </button>
        </div>
      </div>

      {open && hasSubtopics && (
        <div className="bg-[#f8f9fc]">
          {subtopics.map(sub => (
            <SubtopicRow
              key={sub.id}
              subjectId={subjectId}
              moduleId={moduleId}
              topicId={topicId}
              subtopicId={sub.id}
              subtopicName={sub.name}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// ModuleAccordion
// ─────────────────────────────────────────────
function ModuleAccordion({
  subjectId, module, defaultOpen, statsByTopic,
}: {
  subjectId: string;
  module: { id: string; title: string; icon?: ElementType; topics: { id: string; name: string; questionCount: number; subtopics?: { id: string; name: string }[] }[] };
  defaultOpen: boolean;
  statsByTopic: Record<string, { attempted: number; correct: number; accuracy: number }>;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const Icon: ElementType = typeof module.icon === "function" ? module.icon : BookOpen;

  const moduleAttempted = module.topics.reduce(
    (acc, t) => acc + (statsByTopic[t.id]?.attempted ?? 0), 0
  );
  const moduleCorrect = module.topics.reduce(
    (acc, t) => acc + (statsByTopic[t.id]?.correct ?? 0), 0
  );
  const moduleAccuracy = moduleAttempted > 0
    ? Math.round((moduleCorrect / moduleAttempted) * 100)
    : null;

  return (
    <div className="bg-white border border-[#dde3ed] rounded-2xl overflow-hidden shadow-sm">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between p-4 bg-[#1a3a6b] hover:bg-[#12265a] transition-colors text-white"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-bold text-base">{module.title}</h3>
            <p className="text-xs text-white/60">{module.topics.length} topics</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {moduleAccuracy !== null && (
            <div className="text-right">
              <div className="text-lg font-black text-[#f5a623]">{moduleAccuracy}%</div>
              <div className="text-xs text-white/50">{moduleAttempted} practiced</div>
            </div>
          )}
          {open
            ? <ChevronUp className="w-5 h-5 text-white/50" />
            : <ChevronDown className="w-5 h-5 text-white/50" />}
        </div>
      </button>

      {open && (
        <div className="divide-y divide-[#eef1f6]">
          {module.topics.map(topic => (
            <TopicRow
              key={topic.id}
              subjectId={subjectId}
              moduleId={module.id}
              topicId={topic.id}
              topicName={topic.name}
              subtopics={(topic as any).subtopics}
              topicStats={statsByTopic[topic.id] ?? null}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────
export function SubjectDetailPage() {
  const [subjectId, setSubjectId] = useState<string>("");
  const { stats } = useAuth();
  const { subjects, loading: subjectsLoading } = useSubjects();
  const [activeTab, setActiveTab] = useState<"practice" | "analytics">("practice");

  useEffect(() => {
    const parts = window.location.pathname.split("/");
    if (parts.length >= 3) setSubjectId(parts[2] ?? "");
  }, []);

  const currentSubject = subjects.find(s => s.id === subjectId);

  const statsBySubject = stats?.statsBySubject ?? {};
  const statsByTopic   = stats?.statsByTopic   ?? {};

  if (subjectsLoading) {
    return (
      <div className="min-h-screen bg-[#f8f9fc] flex items-center justify-center">
        <div className="text-[#5a6a7e]">Loading…</div>
      </div>
    );
  }

  if (!currentSubject) {
    return (
      <div className="min-h-screen bg-[#f8f9fc] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#1a2435] mb-4">Subject Not Found</h2>
          <button
            onClick={() => (window.location.href = "/subjects")}
            className="bg-[#1a3a6b] text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-[#12265a] transition-colors"
          >
            Back to Question Banks
          </button>
        </div>
      </div>
    );
  }

  if (currentSubject.comingSoon) {
    return (
      <div className="min-h-screen bg-[#f8f9fc] flex items-center justify-center px-4">
        <div className="bg-white border border-[#dde3ed] rounded-2xl shadow-sm p-10 w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
            <span className="text-3xl">⏳</span>
          </div>
          <div className="inline-block bg-amber-100 text-amber-700 border border-amber-200 text-xs font-bold px-3 py-1 rounded-full mb-4">
            Coming Soon
          </div>
          <h2 className="text-2xl font-extrabold text-[#1a2435] mb-2">{currentSubject.title} Question Bank</h2>
          <p className="text-[#5a6a7e] text-sm mb-6 leading-relaxed">
            This question bank is still in development. Expert tutors for {currentSubject.title} are available now for live 1:1 sessions.
          </p>
          <a
            href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/"
            target="_blank"
            rel="noreferrer"
            className="w-full flex items-center justify-center bg-[#f5a623] hover:bg-[#e09310] text-[#12265a] font-bold py-2.5 rounded-xl transition-colors text-sm mb-3"
          >
            Book a Free Tutoring Consultation
          </a>
          <button
            onClick={() => (window.location.href = "/subjects")}
            className="w-full border border-[#dde3ed] rounded-xl py-2.5 text-sm font-semibold text-[#1a2435] hover:bg-[#f8f9fc] transition-colors"
          >
            Back to Question Banks
          </button>
        </div>
      </div>
    );
  }

  const totalQ = currentSubject.modules.reduce(
    (acc, m) => acc + m.topics.reduce((a, t) => a + (t.questionCount || 0), 0), 0
  );
  const subjectStats = statsBySubject[currentSubject.id];
  const hasData = subjectStats && subjectStats.attempted > 0;

  return (
    <div className="min-h-screen bg-[#f8f9fc] flex flex-col">
      <Header />

      {/* ── Subject hero banner ── */}
      <div className={`bg-gradient-to-r ${currentSubject.gradient}`}>
        <div className="max-w-6xl mx-auto px-6 py-8">
          <button
            onClick={() => (window.location.href = "/subjects")}
            className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-medium mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All Question Banks
          </button>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-white mb-1">{currentSubject.title}</h1>
              <p className="text-white/70 text-sm">{currentSubject.description}</p>
            </div>
            <div className="flex gap-5 flex-shrink-0">
              {totalQ > 0 && (
                <div className="text-center">
                  <div className="text-2xl font-black text-white">{totalQ.toLocaleString()}+</div>
                  <div className="text-white/60 text-xs">Questions</div>
                </div>
              )}
              {hasData && (
                <>
                  <div className="w-px bg-white/20" />
                  <div className="text-center">
                    <div className="text-2xl font-black text-white">{subjectStats.attempted}</div>
                    <div className="text-white/60 text-xs">Practiced</div>
                  </div>
                  <div className="w-px bg-white/20" />
                  <div className="text-center">
                    <div className="text-2xl font-black text-white">{subjectStats.accuracy.toFixed(0)}%</div>
                    <div className="text-white/60 text-xs">Accuracy</div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="bg-white border-b border-[#dde3ed] sticky top-16 z-40">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex gap-0">
            {[
              { key: "practice" as const,  label: "Practice",  icon: Play },
              { key: "analytics" as const, label: "Analytics", icon: BarChart3 },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold border-b-2 transition-colors ${
                  activeTab === key
                    ? "border-[#1a3a6b] text-[#1a3a6b]"
                    : "border-transparent text-[#5a6a7e] hover:text-[#1a2435]"
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-10">

        {/* ── Analytics tab ── */}
        {activeTab === "analytics" && (
          <SubjectAnalytics
            subject={currentSubject}
            statsBySubject={statsBySubject}
            statsByTopic={statsByTopic}
          />
        )}

        {/* ── Practice tab ── */}
        {activeTab === "practice" && (
          <>
            <div className="mb-8 flex items-start gap-2 text-sm text-[#5a6a7e] bg-white border border-[#dde3ed] rounded-xl px-4 py-3">
              <Play className="w-4 h-4 mt-0.5 shrink-0 text-[#1a3a6b]" />
              <span>
                Choose a topic and hit <strong className="text-[#1a2435]">Practice</strong> to start a session, or <strong className="text-[#1a2435]">Review</strong> to go through past sessions.
                Filter by difficulty with the pill buttons.
              </span>
            </div>
            <div className="space-y-5">
              {currentSubject.modules.map((module, i) => (
                <ModuleAccordion
                  key={module.id}
                  subjectId={currentSubject.id}
                  module={module}
                  defaultOpen={i === 0}
                  statsByTopic={statsByTopic}
                />
              ))}
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[#dde3ed] bg-white mt-10 py-6">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#5a6a7e]">
          <div className="flex items-center gap-2">
            <img src="/logo.svg" alt="Scholar Sphere" className="h-6" />
            <span>© {new Date().getFullYear()} Scholar Sphere</span>
          </div>
          <div className="flex gap-5">
            <a href="/subjects" className="hover:text-[#1a3a6b]">Question Banks</a>
            <a href="/dashboard" className="hover:text-[#1a3a6b]">Dashboard</a>
            <a href="/privacy" className="hover:text-[#1a3a6b]">Privacy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
